/**
 * @fileoverview Global Winbit32 wallet bar.
 *
 * A calm, always-bottom strip that doubles as the site's wallet presence.
 * The shielded wallet functionality is lifted from Winbit32, so we lean into
 * that branding: when an in-browser (winbit32) wallet is connected the bar
 * shows a persistent, slim "Winbit32 · Connected" strip with the live
 * balance — the feel of a wallet the site is plugged into, but as a bottom
 * bar rather than the floating windows Winbit32 itself uses.
 *
 * During activity it expands to make the in-browser Orchard note search and
 * shielded-send visible (previously the only feedback during a mint was a
 * frozen "Picking…" button while the wallet silently scanned for minutes).
 *
 * It subscribes directly to the wallet singleton's scan-progress and
 * connection channels (no React context required). This component is written
 * to be self-contained so it can later be lifted into a standalone Winbit32
 * wallet module / browser extension.
 */

import { useEffect, useRef, useState } from 'react'
import {
	ArrowPathIcon,
	CheckCircleIcon,
	ClockIcon,
	ExclamationTriangleIcon,
	MagnifyingGlassIcon,
	XMarkIcon,
} from '@heroicons/react/24/outline'

import { zcashWallet } from '../../services/zcashWallet'
import type { ScanProgress } from '../../services/winbit32Wallet'

// How long the "Scan complete" confirmation lingers as a full bar before it
// collapses back to the slim connected strip (or hides entirely).
const COMPLETE_LINGER_MS = 4000

// Keep the activity bar (and its blur shield) above the mint modals (z-[60])
// so the bar — the "winbit32 bar" — is never itself blurred by them.
const BAR_Z = 'z-[70]'

// Cap on the rolling status/error log so a long mint can't grow it unbounded.
const HISTORY_LIMIT = 60

interface HistoryEntry {
	id: number
	ts: number
	text: string
	kind: 'info' | 'error' | 'done'
}

const fmtClock = (ts: number): string => {
	try {
		return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
	} catch {
		return ''
	}
}

/**
 * Dropdown log of recent wallet status/error messages. The live bar only ever
 * shows the latest line, so a transient error (e.g. "no notes large enough")
 * would otherwise vanish on the next scan tick — this keeps a reviewable trail.
 */
function HistoryPanel({
	open,
	history,
	onClear,
}: {
	open: boolean
	history: HistoryEntry[]
	onClear: () => void
}) {
	if (!open || history.length === 0) return null
	return (
		<div className="absolute bottom-full inset-x-0 mb-1 px-4 sm:px-6 lg:px-8">
			<div className="mx-auto max-w-7xl overflow-hidden rounded-lg border border-white/10 bg-slate-900/95 shadow-xl backdrop-blur">
				<div className="flex items-center justify-between border-b border-white/10 px-3 py-2">
					<span className="text-xs font-semibold text-slate-200">Wallet activity</span>
					<button
						type="button"
						onClick={onClear}
						className="text-[11px] text-slate-400 hover:text-slate-200"
					>
						Clear
					</button>
				</div>
				<ul className="max-h-64 divide-y divide-white/5 overflow-y-auto">
					{history
						.slice()
						.reverse()
						.map((h) => (
							<li key={h.id} className="flex items-start gap-2 px-3 py-1.5 text-xs">
								<span className="flex-shrink-0 tabular-nums text-slate-500">{fmtClock(h.ts)}</span>
								<span
									className={
										h.kind === 'error'
											? 'text-red-300'
											: h.kind === 'done'
												? 'text-emerald-300'
												: 'text-slate-300'
									}
								>
									{h.text}
								</span>
							</li>
						))}
				</ul>
			</div>
		</div>
	)
}

/** Inline button that toggles the {@link HistoryPanel}. Hidden when empty. */
function HistoryToggle({
	count,
	open,
	onToggle,
}: {
	count: number
	open: boolean
	onToggle: () => void
}) {
	if (count === 0) return null
	return (
		<button
			type="button"
			onClick={onToggle}
			className={`flex-shrink-0 inline-flex items-center gap-1 rounded-md border px-1.5 py-1 text-[11px] ${
				open ? 'border-white/30 bg-white/10 text-slate-100' : 'border-white/15 text-slate-300 hover:bg-white/10'
			}`}
			aria-label={open ? 'Hide wallet activity log' : 'Show wallet activity log'}
			title="Recent wallet activity"
		>
			<ClockIcon className="h-4 w-4" />
			<span className="tabular-nums">{count}</span>
		</button>
	)
}

// Brand logo lives in /public (Winbit32's own app icon). Referenced as a
// runtime URL so no bundler import is required if this is extracted later.
const WINBIT32_LOGO = '/winbit32-logo.png'
const WINBIT32_URL = 'https://winbit32.com'

type Phase = 'idle' | 'busy' | 'done' | 'error'

interface ConnSnapshot {
	walletType: string
	shielded: number
	transparent: number
}

function phaseOf(progress: ScanProgress | null): Phase {
	if (!progress) return 'idle'
	if (progress.error) return 'error'
	if (progress.isScanning) return 'busy'
	if (progress.status && progress.status !== 'idle') return 'done'
	return 'idle'
}

function readConnection(): ConnSnapshot | null {
	try {
		const c = zcashWallet.getConnection()
		if (!c || !c.isConnected) return null
		return {
			walletType: c.walletType,
			shielded: Number(c.balance?.shielded) || 0,
			transparent: Number(c.balance?.transparent) || 0,
		}
	} catch {
		return null
	}
}

function sameConn(a: ConnSnapshot | null, b: ConnSnapshot | null): boolean {
	if (a === b) return true
	if (!a || !b) return false
	return a.walletType === b.walletType && a.shielded === b.shielded && a.transparent === b.transparent
}

const fmtZec = (v: number): string => {
	if (!Number.isFinite(v)) return '0'
	return v < 0.001 ? v.toFixed(4) : v.toFixed(3)
}

/**
 * Winbit32 logo + wordmark. The wordmark is a new-tab link to winbit32.com so
 * the bar reads as "powered by the Winbit32 wallet". The logo hides nothing;
 * the wordmark hides on very small screens.
 */
function Winbit32Brand({ subtitle }: { subtitle?: string }) {
	return (
		<a
			href={WINBIT32_URL}
			target="_blank"
			rel="noopener noreferrer"
			title="Winbit32 — opens winbit32.com in a new tab"
			className="flex items-center gap-2.5 flex-shrink-0 group"
		>
			<img
				src={WINBIT32_LOGO}
				alt="Winbit32"
				width={28}
				height={28}
				draggable={false}
				className="h-7 w-7 object-contain drop-shadow"
			/>
			<div className="leading-tight hidden sm:block">
				<div className="text-[13px] font-semibold tracking-tight text-slate-100 group-hover:text-white group-hover:underline underline-offset-2 decoration-white/40">
					Winbit32
				</div>
				{subtitle ? (
					<div className="text-[10px] uppercase tracking-wider text-slate-400">{subtitle}</div>
				) : null}
			</div>
		</a>
	)
}

export function WalletStatusBar() {
	const [progress, setProgress] = useState<ScanProgress | null>(() => {
		try {
			return zcashWallet.getScanProgress()
		} catch {
			return null
		}
	})
	const [conn, setConn] = useState<ConnSnapshot | null>(() => readConnection())
	const [visible, setVisible] = useState(false)
	const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
	// Remember a dismissed error so the same message doesn't pop straight
	// back on the next progress tick (the singleton keeps the last error).
	const dismissedError = useRef<string | null>(null)
	// Rolling log of status/error lines so transient messages (which the live
	// bar overwrites on the next tick) remain reviewable.
	const [history, setHistory] = useState<HistoryEntry[]>([])
	const [showHistory, setShowHistory] = useState(false)
	const historyId = useRef(0)
	const lastHistoryText = useRef<string | null>(null)
	// Normalised form of the last logged line (volatile bits stripped) so we can
	// collapse a stream of "Scanning notes… 45s/50s/55s" ticks into one row.
	const lastHistoryNorm = useRef<string | null>(null)
	// First scanned height seen in the current busy episode, so we can show a
	// determinate bar from block deltas before the engine reports a percent.
	const scanStartHeight = useRef<number | null>(null)

	// Keep the connection snapshot fresh on connect/disconnect AND on every
	// scan-progress tick (balance is refreshed as part of a scan), deduping so
	// we don't churn renders.
	const refreshConn = () => {
		setConn((prev) => {
			const next = readConnection()
			return sameConn(prev, next) ? prev : next
		})
	}

	useEffect(() => {
		// Strip the volatile parts of a status (elapsed seconds, percent, block
		// numbers) so consecutive ticks of the same logical step share a key.
		const normalize = (t: string): string =>
			t
				.toLowerCase()
				.replace(/[\d.,]+\s*%/g, '')
				.replace(/[\d.,]+\s*s(ec(ond)?s?)?\b/g, '')
				.replace(/block\s+[\d.,]+\s*\/\s*[\d.,]+/g, 'block')
				.replace(/[\d.,]+/g, '')
				.replace(/elapsed|·|\.\.\.|…/g, '')
				.replace(/\s+/g, ' ')
				.trim()

		const recordHistory = (next: ScanProgress) => {
			const text = (next.error || next.status || '').trim()
			if (!text || text === 'idle') return
			const kind: HistoryEntry['kind'] = next.error
				? 'error'
				: next.isScanning === false
					? 'done'
					: 'info'
			const norm = `${kind}|${normalize(text)}`
			// Same logical line as the last one (e.g. repeated scan ticks): refresh
			// the existing row's text/time in place instead of stacking duplicates.
			if (norm === lastHistoryNorm.current) {
				lastHistoryText.current = text
				setHistory((prev) => {
					if (prev.length === 0) return prev
					const copy = prev.slice()
					copy[copy.length - 1] = { ...copy[copy.length - 1], ts: Date.now(), text }
					return copy
				})
				return
			}
			lastHistoryNorm.current = norm
			lastHistoryText.current = text
			historyId.current += 1
			const entry: HistoryEntry = { id: historyId.current, ts: Date.now(), text, kind }
			setHistory((prev) => {
				const merged = [...prev, entry]
				return merged.length > HISTORY_LIMIT ? merged.slice(merged.length - HISTORY_LIMIT) : merged
			})
		}
		const offProgress = zcashWallet.onScanProgress((next) => {
			setProgress({ ...next })
			refreshConn()
			recordHistory(next)
		})
		const offConn = zcashWallet.onConnectionChange(refreshConn)
		return () => {
			offProgress()
			offConn()
		}
	}, [])

	const phase = phaseOf(progress)

	// Track the start height of the current busy episode (reset when not busy).
	useEffect(() => {
		if (phase !== 'busy') {
			scanStartHeight.current = null
			return
		}
		if (scanStartHeight.current === null && progress?.scannedHeight) {
			scanStartHeight.current = progress.scannedHeight
		}
	}, [phase, progress?.scannedHeight])

	useEffect(() => {
		if (hideTimer.current) {
			clearTimeout(hideTimer.current)
			hideTimer.current = null
		}

		if (phase === 'busy') {
			setVisible(true)
			return
		}
		if (phase === 'error') {
			if (progress?.error && dismissedError.current === progress.error) {
				setVisible(false)
			} else {
				setVisible(true)
			}
			return
		}
		if (phase === 'done') {
			setVisible(true)
			hideTimer.current = setTimeout(() => setVisible(false), COMPLETE_LINGER_MS)
			return
		}
		setVisible(false)
	}, [phase, progress?.error, progress?.status])

	useEffect(() => {
		return () => {
			if (hideTimer.current) clearTimeout(hideTimer.current)
		}
	}, [])

	// The slim, persistent presence: an in-browser Winbit32 wallet is connected
	// and there's no louder activity to show right now.
	const isWinbit32Connected = conn?.walletType === 'winbit32'
	const showActivity = visible && !!progress
	const showConnectedStrip = !showActivity && isWinbit32Connected

	if (!showActivity && !showConnectedStrip) return null

	// ── Persistent connected strip ───────────────────────────────────────────
	if (showConnectedStrip && conn) {
		const total = conn.shielded + conn.transparent
		return (
			<div
				role="status"
				aria-live="polite"
				className={`fixed inset-x-0 bottom-0 ${BAR_Z} border-t border-white/10 bg-slate-900/95 text-slate-100 text-sm shadow-lg backdrop-blur`}
			>
				<HistoryPanel open={showHistory} history={history} onClear={() => setHistory([])} />
				<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-2 flex items-center gap-3">
					<Winbit32Brand subtitle="Wallet" />
					<span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-300 ring-1 ring-emerald-400/20">
						<span className="h-1.5 w-1.5 rounded-full bg-emerald-400" aria-hidden="true" />
						Connected
					</span>
					<div className="ml-auto flex items-center gap-3 text-xs tabular-nums">
						<HistoryToggle count={history.length} open={showHistory} onToggle={() => setShowHistory((v) => !v)} />
						<span className="font-medium text-slate-100">{fmtZec(total)} ZEC</span>
						<span className="hidden sm:inline text-slate-400">
							shielded {fmtZec(conn.shielded)} · transparent {fmtZec(conn.transparent)}
						</span>
					</div>
				</div>
			</div>
		)
	}

	// ── Activity bar ───────────────────────────────────────────────────────────
	const status = progress!.status || 'Working…'
	const statusHasPercent = /%/.test(status)
	const enginePercent = Math.max(0, Math.min(100, Number(progress!.percent) || 0))
	// Fall back to a block-delta estimate so the orange bar fills with real
	// progress before the engine's percentComplete arrives (it can lag minutes
	// on a cold cache). Anchored on the first height seen this episode.
	const startHeight = scanStartHeight.current
	const curHeight = Number(progress!.scannedHeight) || 0
	const latHeight = Number(progress!.latestHeight) || 0
	const blockPercent =
		startHeight !== null && latHeight > startHeight && curHeight >= startHeight
			? Math.max(0, Math.min(100, ((curHeight - startHeight) / (latHeight - startHeight)) * 100))
			: 0
	const percent = enginePercent > 0 ? enginePercent : blockPercent
	const showPercent = !statusHasPercent && percent > 0 && percent < 100
	const determinate = percent > 0
	const heightDetail =
		progress!.scannedHeight && progress!.latestHeight
			? `block ${progress!.scannedHeight.toLocaleString()} / ${progress!.latestHeight.toLocaleString()}`
			: null

	const tone =
		phase === 'error'
			? 'bg-red-950/95 border-red-500/40'
			: phase === 'done'
				? 'bg-emerald-950/90 border-emerald-500/30'
				: 'bg-slate-900/95 border-white/10'

	const Icon =
		phase === 'error'
			? ExclamationTriangleIcon
			: phase === 'done'
				? CheckCircleIcon
				: status.toLowerCase().includes('select')
					? MagnifyingGlassIcon
					: ArrowPathIcon

	const dismissable = phase === 'error' || phase === 'done'

	return (
		<div
			role="status"
			aria-live="polite"
			className={`fixed inset-x-0 bottom-0 ${BAR_Z} border-t text-slate-100 text-sm shadow-lg backdrop-blur ${tone}`}
		>
			<HistoryPanel open={showHistory} history={history} onClear={() => setHistory([])} />
			{/* Top progress hairline: determinate width when we have a percent,
			    otherwise an indeterminate sweep so the user still sees motion. */}
			<div className="absolute inset-x-0 top-0 h-1 overflow-hidden bg-white/5">
				{phase === 'busy' && !determinate ? (
					<div className="h-full w-1/3 animate-sweep bg-zcash-400/80" />
				) : (
					<div
						className={`h-full bg-zcash-400 transition-[width] duration-500 ${
							phase === 'error' ? 'bg-red-400' : phase === 'done' ? 'bg-emerald-400' : ''
						}`}
						style={{ width: `${phase === 'done' ? 100 : percent}%` }}
					/>
				)}
			</div>

			<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-2.5 flex items-center gap-3">
				{isWinbit32Connected ? (
					<>
						<Winbit32Brand />
						<div className="h-6 w-px bg-white/10 flex-shrink-0" aria-hidden="true" />
					</>
				) : null}
				<Icon
					className={`h-5 w-5 flex-shrink-0 ${
						phase === 'busy' ? 'animate-spin text-zcash-300' : phase === 'error' ? 'text-red-300' : 'text-emerald-300'
					}`}
					aria-hidden="true"
				/>
				<div className="min-w-0 flex-1">
					<div className="flex items-center gap-2 flex-wrap">
						<span className="font-medium truncate">{status}</span>
						{showPercent ? (
							<span className="text-xs text-slate-300 tabular-nums">{percent.toFixed(0)}%</span>
						) : null}
						{heightDetail ? (
							<span className="text-xs text-slate-400 tabular-nums">· {heightDetail}</span>
						) : null}
					</div>
					{phase === 'error' && progress!.error ? (
						<p className="text-xs text-red-200/90 mt-0.5 break-words">{progress!.error}</p>
					) : phase === 'busy' ? (
						<p className="text-xs text-slate-400 mt-0.5">
							Searching the chain for your shielded notes — this stays open while it runs.
						</p>
					) : null}
				</div>
				<HistoryToggle count={history.length} open={showHistory} onToggle={() => setShowHistory((v) => !v)} />
				{dismissable ? (
					<button
						type="button"
						onClick={() => {
							if (phase === 'error' && progress!.error) {
								dismissedError.current = progress!.error
							}
							setVisible(false)
						}}
						className="flex-shrink-0 inline-flex items-center rounded-md border border-white/20 p-1 text-slate-300 hover:bg-white/10"
						aria-label="Dismiss"
					>
						<XMarkIcon className="h-4 w-4" />
					</button>
				) : null}
			</div>
		</div>
	)
}

export default WalletStatusBar
